/**
 * Opportunity map (content-first redesign) — the portfolio-posture view for the
 * 法规×痛点×草案 analysis: "does this config have any topic where market pain
 * and regulatory motion coincide, or is everything baseline?"
 *
 * Honest two-mode design (the founder's 视觉表达优化 ask):
 *   MODE A (pain not collected yet — the common state): a 1D 草案活跃度 swarm.
 *     X = how recently each topic's latest draft was fetched (本周 → 更早);
 *     topics with no drafts collapse into a left-gutter cluster whose caption IS
 *     the finding ("N 个主题 · 严格存量法 · 本期无草案动向"). No fake quadrants,
 *     no bubbles glued to a floor pretending to be 2D data.
 *   MODE B (any pain collected): X = draft activity (count), Y = pain intensity,
 *     real quadrant frame + captions.
 *
 * Encodings that carry actual bits: bubble fill = DOMAIN hue (verdict fill was
 * uniform watch-yellow and flirted with implying verdicts — ADR-0002); size =
 * signal volume; an outline ring = this topic has a session-cached live
 * diagnosis (color = its type). Top movers get ALWAYS-ON labels with the real
 * draft headline. The map itself never fires live LLM calls — its detail card
 * routes to the briefing row.
 */

import { useMemo, useState } from "react";

import type { OpportunityRadarEntry } from "../services/api";
import { useManualRadar } from "../lib/useManualRadar";
import {
  domainColor,
  parseCountryTag,
  resolveBubbleCollisions,
  topicLabel,
} from "../lib/radar-view";
import { getCachedBrief } from "../lib/brief-cache";

const W = 680;
const M = { top: 36, right: 24, bottom: 46, left: 24 };
const GUTTER_W = 150;

function painScoreOf(r: OpportunityRadarEntry): number {
  return r.intensity === "high" ? 3 : r.intensity === "med" ? 2 : r.intensity === "low" ? 1 : 0;
}
function volumeOf(r: OpportunityRadarEntry): number {
  return (r.draftCount ?? 0) + (r.intelCount ?? 0);
}
function radiusOf(r: OpportunityRadarEntry): number {
  return Math.min(12, 4 + 2 * Math.sqrt(volumeOf(r)));
}
function daysSince(iso: string, nowMs: number): number {
  return Math.max(0, (nowMs - Date.parse(iso)) / 86_400_000);
}
const RING_COLOR: Record<string, string> = {
  risk: "#e5484d",
  opportunity: "#30a46c",
  watch: "#d9a200",
  none: "#8b8d98",
};

export function OpportunityBubbleChart({ signalCount }: { signalCount: number }) {
  const { status, radar, disclaimer, error, ranAt, isStale, staleCount, run } =
    useManualRadar(signalCount);
  const [hovered, setHovered] = useState<string | null>(null);
  const nowMs = Date.now();

  const withDrafts = radar.filter((r) => (r.draftCount ?? 0) > 0);
  const noDrafts = radar.filter((r) => (r.draftCount ?? 0) === 0);
  const anyPain = radar.some((r) => (r.intelCount ?? 0) > 0);
  const H = anyPain ? 440 : 300;
  const PX0 = M.left + GUTTER_W;
  const PX1 = W - M.right;
  const PY0 = M.top;
  const PY1 = H - M.bottom;

  // Layout: Mode A places drafts by recency on X (right = newest) inside a band;
  // Mode B places by draftCount (X) × painScore (Y). Collision-resolved so
  // coincident topics spread instead of stacking.
  const placed = useMemo(() => {
    const maxDraft = Math.max(1, ...withDrafts.map((r) => r.draftCount ?? 0));
    const seeds = withDrafts.map((r) => {
      const rr = radiusOf(r);
      if (!anyPain) {
        const d = r.latestDraft ? Math.min(60, daysSince(r.latestDraft.fetchedAt, nowMs)) : 60;
        const x = PX1 - (d / 60) * (PX1 - PX0 - 20) - 10;
        return { id: r.topicId, x, y: (PY0 + PY1) / 2, r: rr };
      }
      const x = PX0 + ((r.draftCount ?? 0) / maxDraft) * (PX1 - PX0 - 30) + 15;
      const y = PY1 - (painScoreOf(r) / 3) * (PY1 - PY0 - 20) - 10;
      return { id: r.topicId, x, y, r: rr };
    });
    const packed = resolveBubbleCollisions(seeds, { x0: PX0, y0: PY0, x1: PX1, y1: PY1 });
    return new Map(packed.map((p) => [p.id, p]));
  }, [withDrafts, anyPain, nowMs, PX0, PX1, PY0, PY1]);

  // Always-on labels for the top movers (only ~17 topics carry drafts, so a
  // handful of real headlines fit without the hover-only mush).
  const labeled = useMemo(() => {
    const ranked = [...withDrafts].sort(
      (a, b) =>
        painScoreOf(b) - painScoreOf(a) ||
        (b.draftCount ?? 0) - (a.draftCount ?? 0) ||
        (b.latestDraft?.fetchedAt ?? "").localeCompare(a.latestDraft?.fetchedAt ?? ""),
    );
    return new Set(ranked.slice(0, 5).map((r) => r.topicId));
  }, [withDrafts]);

  const hoveredEntry = hovered ? radar.find((r) => r.topicId === hovered) ?? null : null;
  const domains = [...new Set(withDrafts.map((r) => r.domainId))].sort();

  return (
    <section data-testid="opportunity-bubble-chart" className="bubble-chart">
      <header className="cross-matrix__head">
        <h2>机会地图 · 法规 × 痛点 × 草案</h2>
        <span className="advisory-tag" data-testid="obc-advisory-tag">
          advisory · 非合规结论
        </span>
      </header>

      <div className="analysis-controls">
        {status === "idle" ? (
          <>
            <button type="button" data-testid="obc-run" className="analysis-run-btn" onClick={run}>
              ▶ 运行机会地图
            </button>
            <span className="analysis-hint">先载入 WP29 真实 feed / 扫描痛点，再运行。</span>
          </>
        ) : (
          <>
            <button
              type="button"
              data-testid="obc-rerun"
              className="analysis-run-btn"
              onClick={run}
              disabled={status === "loading"}
            >
              {status === "loading" ? "分析中…" : "↻ 重新分析"}
            </button>
            {ranAt && <span className="analysis-ranat">上次分析 {ranAt}</span>}
          </>
        )}
      </div>

      {isStale && (
        <p role="status" data-testid="obc-stale" className="analysis-stale">
          有 {staleCount} 条信号变动入审查队列，建议重新分析以刷新结果。
        </p>
      )}
      {status === "loading" && (
        <p role="status" className="cross-matrix__loading">
          分析中…（扫描全部法规主题）
        </p>
      )}
      {status === "error" && (
        <p role="alert" className="cross-matrix__error">
          机会地图加载失败：{error}
        </p>
      )}

      {status === "ok" && !anyPain && (
        <p className="bubble-chart__banner" data-testid="obc-pain-banner">
          痛点轴为空 — 在 Discovery &amp; Review 运行「扫描本配置全部痛点」后，本图展开为
          痛点×草案 四象限并显示买家引述。下图先按<strong>草案活跃度</strong>呈现本期动向。
        </p>
      )}

      {status === "ok" && radar.length > 0 && (
        <div className="bubble-chart__plotwrap">
          <svg
            className="bubble-chart__svg"
            viewBox={`0 0 ${W} ${H}`}
            role="img"
            aria-label={anyPain ? "机会地图：草案活跃 × 市场痛点" : "草案活跃度时间轴"}
            data-testid="obc-svg"
          >
            <rect x={M.left} y={PY0} width={W - M.left - M.right} height={PY1 - PY0} className="bubble-chart__frame" />

            {/* Left gutter: the no-drafts stock cluster — the caption IS a finding. */}
            <line x1={PX0} y1={PY0} x2={PX0} y2={PY1} className="bubble-chart__mid" />
            <text x={M.left + 8} y={PY0 + 16} className="bubble-chart__quad" data-testid="obc-gutter-caption">
              {noDrafts.length} 个主题
            </text>
            <text x={M.left + 8} y={PY0 + 30} className="bubble-chart__quad">
              严格存量法 · 本期无草案动向
            </text>
            {noDrafts.slice(0, 60).map((r, i) => {
              const cols = 8;
              const cx = M.left + 14 + (i % cols) * 16;
              const cy = PY0 + 48 + Math.floor(i / cols) * 16;
              if (cy > PY1 - 8) return null;
              return (
                <circle
                  key={r.topicId}
                  cx={cx}
                  cy={cy}
                  r={4}
                  fill={domainColor(r.domainId)}
                  fillOpacity={0.35}
                >
                  <title>{`${topicLabel(r.topicId).zh} · 现行法 ×${r.ruleCount ?? 0}`}</title>
                </circle>
              );
            })}

            {/* Axis ticks / quadrant frame */}
            {!anyPain ? (
              <>
                {[
                  { d: 0, label: "本周" },
                  { d: 30, label: "30天内" },
                  { d: 60, label: "更早" },
                ].map((t) => {
                  const x = PX1 - (t.d / 60) * (PX1 - PX0 - 20) - 10;
                  return (
                    <g key={t.d}>
                      <line x1={x} y1={PY1} x2={x} y2={PY1 + 4} className="bubble-chart__mid" />
                      <text x={x} y={PY1 + 18} textAnchor="middle" className="bubble-chart__axis">
                        {t.label}
                      </text>
                    </g>
                  );
                })}
                <text x={(PX0 + PX1) / 2} y={H - 8} textAnchor="middle" className="bubble-chart__axis">
                  草案收录时间 → 越右越新
                </text>
              </>
            ) : (
              <>
                <line x1={(PX0 + PX1) / 2} y1={PY0} x2={(PX0 + PX1) / 2} y2={PY1} className="bubble-chart__mid" />
                <line x1={PX0} y1={(PY0 + PY1) / 2} x2={PX1} y2={(PY0 + PY1) / 2} className="bubble-chart__mid" />
                <text x={PX0 + 8} y={PY0 + 16} className="bubble-chart__quad">蓝海 · 差异化机会</text>
                <text x={PX1 - 8} y={PY0 + 16} textAnchor="end" className="bubble-chart__quad">红海 · 合规风险</text>
                <text x={PX0 + 8} y={PY1 - 8} className="bubble-chart__quad">低优先 · 观察</text>
                <text x={PX1 - 8} y={PY1 - 8} textAnchor="end" className="bubble-chart__quad">合规底线</text>
                <text x={(PX0 + PX1) / 2} y={H - 8} textAnchor="middle" className="bubble-chart__axis">
                  草案活跃度 →
                </text>
              </>
            )}

            {/* Draft bubbles */}
            {withDrafts.map((r) => {
              const pos = placed.get(r.topicId);
              if (!pos) return null;
              const cached = getCachedBrief(r.topicId);
              const isHot = hovered === r.topicId;
              const headline = r.latestDraft ? parseCountryTag(r.latestDraft.title).headline : "";
              return (
                <g
                  key={r.topicId}
                  data-testid={`obc-bubble-${r.topicId}`}
                  className="bubble-chart__bubble"
                  tabIndex={0}
                  onMouseEnter={() => setHovered(r.topicId)}
                  onMouseLeave={() => setHovered((h) => (h === r.topicId ? null : h))}
                  onFocus={() => setHovered(r.topicId)}
                  onBlur={() => setHovered((h) => (h === r.topicId ? null : h))}
                >
                  <title>{`${topicLabel(r.topicId).zh}｜${headline.slice(0, 80)}`}</title>
                  {cached && (
                    <circle
                      cx={pos.x}
                      cy={pos.y}
                      r={pos.r + 3}
                      fill="none"
                      stroke={RING_COLOR[cached.opportunity.type] ?? RING_COLOR.none}
                      strokeWidth={2}
                      data-testid={`obc-ring-${r.topicId}`}
                    />
                  )}
                  <circle
                    cx={pos.x}
                    cy={pos.y}
                    r={pos.r}
                    fill={domainColor(r.domainId)}
                    fillOpacity={isHot ? 0.95 : 0.6}
                    stroke={domainColor(r.domainId)}
                    strokeOpacity={0.9}
                    strokeWidth={1}
                  />
                  {(labeled.has(r.topicId) || isHot) && (
                    <text
                      x={pos.x}
                      y={pos.y - pos.r - 5 < PY0 + 10 ? pos.y + pos.r + 12 : pos.y - pos.r - 5}
                      textAnchor="middle"
                      className="bubble-chart__label"
                    >
                      {topicLabel(r.topicId).zh}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>

          {/* Domain legend (verdict legend killed — it carried zero bits). */}
          <div className="bubble-chart__legend" data-testid="obc-legend">
            {domains.map((d) => (
              <span key={d} className="bubble-chart__legend-item">
                <span className="bubble-chart__swatch" style={{ background: domainColor(d) }} />
                {d}
              </span>
            ))}
            <span className="bubble-chart__legend-note">气泡大小 = 信号数 · 外环 = 已研判</span>
          </div>

          {/* Content detail card (no category echo; routes to the briefing row). */}
          {hoveredEntry && (
            <div className="bubble-chart__detail" data-testid="obc-detail" role="tooltip">
              <strong>{topicLabel(hoveredEntry.topicId).zh}</strong>
              <span className="bubble-chart__detail-domain">{hoveredEntry.domainId}</span>
              {hoveredEntry.latestDraft && (
                <p className="bubble-chart__detail-headline">
                  {parseCountryTag(hoveredEntry.latestDraft.title).headline.slice(0, 90)}
                  <span className="bubble-chart__detail-date">
                    {" "}
                    · {hoveredEntry.latestDraft.fetchedAt.slice(0, 10)}
                  </span>
                </p>
              )}
              {hoveredEntry.painSample && (
                <p className="bubble-chart__detail-quote">“{hoveredEntry.painSample.quote.slice(0, 70)}…”</p>
              )}
              <p className="bubble-chart__detail-counts">
                现行法 ×{hoveredEntry.ruleCount ?? 0} · 草案 ×{hoveredEntry.draftCount ?? 0}
                {(hoveredEntry.intelCount ?? 0) > 0 ? ` · 痛点 ×${hoveredEntry.intelCount}` : ""}
              </p>
              <button
                type="button"
                className="bubble-chart__detail-link"
                data-testid="obc-goto-briefing"
                onClick={() =>
                  document
                    .getElementById(`ab-row-anchor-${hoveredEntry.topicId}`)
                    ?.scrollIntoView({ behavior: "smooth", block: "center" })
                }
              >
                查看简报行（深度研判在那里运行）→
              </button>
            </div>
          )}
        </div>
      )}

      {disclaimer && (
        <p className="cross-matrix__disclaimer" data-testid="obc-disclaimer">
          {disclaimer}
        </p>
      )}
    </section>
  );
}
